import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { ArrowPathIcon, CheckCircleIcon, ClockIcon, ExclamationCircleIcon, QueueListIcon } from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'

import apiClient from '@/lib/api'
import { uiLogger } from '@/lib/debug'
import type { CustomerTask, CustomerTaskStatus } from '@/types/api'

const statusPresentation: Record<CustomerTaskStatus, { label: string; className: string; icon: typeof ClockIcon }> = {
  running: { label: 'Running', className: 'bg-blue-500/10 text-blue-600 dark:text-blue-300', icon: ArrowPathIcon },
  delayed: { label: 'Delayed', className: 'bg-amber-500/10 text-amber-700 dark:text-amber-300', icon: ClockIcon },
  completed: { label: 'Completed', className: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300', icon: CheckCircleIcon },
  failed: { label: 'Failed', className: 'bg-red-500/10 text-red-700 dark:text-red-300', icon: ExclamationCircleIcon },
}

export function customerTaskPollingInterval(open: boolean, activeCount: number): number {
  return open || activeCount > 0 ? 5_000 : 45_000
}

function TaskRow({ task, active }: { task: CustomerTask; active: boolean }) {
  const presentation = statusPresentation[task.status]
  const StatusIcon = presentation.icon
  const progress = typeof task.progress?.progress_percent === 'number'
    ? Math.max(0, Math.min(100, task.progress.progress_percent))
    : null
  const content = (
    <div className={`${active ? 'bg-gray-50 dark:bg-[#2a2a2e]' : ''} px-4 py-3`}>
      <div className="flex items-start gap-3">
        <StatusIcon className={`mt-0.5 h-5 w-5 shrink-0 ${task.status === 'running' ? 'animate-spin text-blue-500' : task.status === 'failed' ? 'text-red-500' : task.status === 'delayed' ? 'text-amber-500' : 'text-emerald-500'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{task.title}</p>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${presentation.className}`}>{presentation.label}</span>
          </div>
          <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
            {task.server?.name || 'Account'} · {formatDistanceToNow(new Date(task.updated_at), { addSuffix: true })}
          </p>
          {(task.progress?.message || task.progress?.phase || task.summary) && (
            <p className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-300">
              {task.summary || task.progress.message || task.progress.phase?.replace(/_/g, ' ')}
            </p>
          )}
          {progress != null && task.status !== 'completed' && (
            <div className="mt-2 flex items-center gap-2">
              <div
                className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
                role="progressbar"
                aria-label={`${task.title} progress`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
              >
                <div className="h-full rounded-full bg-primary-500 transition-[width]" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-[10px] tabular-nums text-gray-500">{progress}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
  return content
}

function CustomerTasksMenuBody({ open }: { open: boolean }) {
  const [tasks, setTasks] = useState<CustomerTask[]>([])
  const [activeCount, setActiveCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const hasLoaded = useRef(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await apiClient.getCustomerTasks({ perPage: 10 })
      setTasks(current => {
        if (hasLoaded.current) {
          const changed = response.data.find(next => {
            const previous = current.find(task => task.batch_uuid === next.batch_uuid)
            return previous && (
              previous.status !== next.status
              || previous.progress?.phase !== next.progress?.phase
              || previous.progress?.message !== next.progress?.message
            )
          })
          if (changed) {
            const detail = changed.summary || changed.progress?.message || changed.progress?.phase?.replace(/_/g, ' ') || statusPresentation[changed.status].label
            setAnnouncement(`${changed.title}: ${detail}`)
          }
        }
        hasLoaded.current = true
        return response.data
      })
      setActiveCount(response.activeCount)
      setFailed(false)
    } catch (error) {
      setFailed(true)
      uiLogger.warn('Unable to load customer tasks', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (open && hasLoaded.current) void load()
  }, [load, open])

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (!document.hidden) void load()
    }
    const refreshOnVisibility = () => {
      if (!document.hidden) void load()
    }
    const interval = window.setInterval(
      refreshWhenVisible,
      customerTaskPollingInterval(open, activeCount),
    )
    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshOnVisibility)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshOnVisibility)
    }
  }, [activeCount, load, open])

  return (
    <>
      <p className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</p>
      <Menu.Button
        className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-[#2a2a2e]"
        title={activeCount ? `${activeCount} active task${activeCount === 1 ? '' : 's'}` : 'Tasks'}
        aria-label="Customer tasks"
      >
        <QueueListIcon className={`h-5 w-5 ${activeCount ? 'text-primary-500' : ''}`} />
        {activeCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-semibold leading-none text-white">
            {activeCount > 99 ? '99+' : activeCount}
          </span>
        )}
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-[100] mt-2 w-[calc(100vw-2rem)] max-w-[22rem] origin-top-right overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-[#252526]">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-[#2d2d2d]">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Tasks</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Recent server operations</p>
            </div>
            <button type="button" onClick={() => void load()} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5" aria-label="Refresh tasks">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100 dark:divide-[#2d2d2d]">
            {loading && tasks.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">Loading tasks…</div>
            ) : failed && tasks.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">Task status is temporarily unavailable.</div>
            ) : tasks.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">No recent tasks.</div>
            ) : tasks.map(task => (
              <Menu.Item key={task.batch_uuid} as={Fragment}>
                {({ active }) => task.link ? (
                  <Link to={task.link}><TaskRow task={task} active={active} /></Link>
                ) : (
                  <div><TaskRow task={task} active={active} /></div>
                )}
              </Menu.Item>
            ))}
          </div>
          <div className="border-t border-gray-100 px-4 py-2 text-center text-[11px] text-gray-500 dark:border-[#2d2d2d] dark:text-gray-400">
            Completed and failed tasks remain visible for seven days.
          </div>
        </Menu.Items>
      </Transition>
    </>
  )
}

export default function CustomerTasksMenu() {
  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => <CustomerTasksMenuBody open={open} />}
    </Menu>
  )
}
